import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import KeyboardArrowRightIcon from "@mui/icons-material/KeyboardArrowRight";
import SearchIcon from "@mui/icons-material/Search";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Badge,
  Box,
  Checkbox,
  CircularProgress,
  FormControlLabel,
  IconButton,
  InputBase,
  Link,
  ListItem,
  Typography,
  useTheme,
} from "@mui/material";
import { fetchTagListing } from "@platformx/authoring-apis";
import { Tag_Icon } from "@platformx/shared/static-assets";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import useGalleryStyle from "./ContentGallery.styles";
import {
  CATEGORY_INITIAL_TAGS_ROWS,
  TAG_SEARCH_DEBOUNCE_MS,
  TAG_SEARCH_THRESHOLD,
  TAG_WINDOW_SIZE,
} from "./utils/constant";

const GalleryLeftSideBar = ({
  categoriesdata,
  updateTagField,
  selectedTags,
  allowed_collection,
  allowed_content,
  allowedCDPTags = [],
  selectedCDPTags,
}) => {
  // Choose only selected Tags
  const updatedSelectedTags: any = [
    ...allowedCDPTags.filter((tag) => selectedCDPTags.includes(tag)),
    ...selectedTags,
  ];
  const { t } = useTranslation();
  const [tagSearch, setTagSearch] = useState("");
  const [visibleCount, setVisibleCount] = useState(TAG_WINDOW_SIZE);
  const [searchState, setSearchState] = useState({
    tags: [] as string[],
    nextStart: 0,
    hasMore: false,
    loading: false,
    failed: false,
  });
  // This category's own tag listing - loaded directly via fetchTagListing (same call the
  // search box below uses, with an empty search string), instead of relying on a `tags` prop
  // sliced out of one cross-category bulk fetch by the parent. Mirrors @platformx/tag-selector's
  // TagSelector: discover categories via fetchCategoryCounts (done by the parent, which still
  // decides which/how many GalleryLeftSideBars to render), then have each category load its own
  // listing via fetchTagListing. Everything below this (search, windowed View More, selected-tag
  // handling, allowed-tags restriction) is unchanged - only where the base list comes from.
  const [initialTagsState, setInitialTagsState] = useState({
    tags: [] as string[],
    loading: true,
    // false when the listing hit CATEGORY_INITIAL_TAGS_ROWS, i.e. the category has more tags
    // than were loaded (e.g. Player) and a selected tag may live beyond that first page.
    complete: true,
  });
  // Tags confirmed to belong to this category but missing from the initial listing - picked up
  // from search results and from resolving selected tags. Without these, a tag selected via
  // search (beyond the first page) is neither counted in the badge nor pinned to the top.
  const [discoveredTags, setDiscoveredTags] = useState<string[]>([]);
  const resolvedSelectedTags = useRef(new Set<string>());
  const latestRequestId = useRef(0);
  const classes = useGalleryStyle();
  const theme = useTheme();

  useEffect(() => {
    let cancelled = false;
    setInitialTagsState({ tags: [], loading: true, complete: true });
    setDiscoveredTags([]);
    resolvedSelectedTags.current = new Set();

    fetchTagListing({
      searchCategory: categoriesdata?.category || "",
      searchString: "",
      start: 0,
      rows: CATEGORY_INITIAL_TAGS_ROWS,
    })
      .then((res: any) => {
        if (cancelled) return;
        const records = Array.isArray(res?.authoring_getTagsList) ? res.authoring_getTagsList : [];
        const names: string[] = records
          .map((record: any) => record?.tag_name || record?.tag_value || "")
          .filter(Boolean);
        setInitialTagsState({
          tags: names,
          loading: false,
          complete: records.length < CATEGORY_INITIAL_TAGS_ROWS,
        });
      })
      .catch(() => {
        if (cancelled) return;
        setInitialTagsState({ tags: [], loading: false, complete: true });
      });

    return () => {
      cancelled = true;
    };
  }, [categoriesdata?.category]);

  const getUpdatedCatogories = () => {
    // Input validation
    if (!categoriesdata || !categoriesdata.category) {
      return { category: "", tags: [] };
    }

    const categoryName = categoriesdata.category.toLowerCase();
    const originalTags = [...initialTagsState.tags]; // Create immutable copy

    // Handle Content Type category
    if (categoryName === "content type") {
      let filteredTags: string[] = [];
      // For other types, use allowed_content to filter tags
      filteredTags =
        allowed_content?.length > 0
          ? originalTags.filter((tag) => allowed_content.includes(tag))
          : []; // Return no tags if allowed_content is empty

      return {
        ...categoriesdata,
        tags: filteredTags,
      };
    }

    // Handle Collection category
    if (categoryName === "collection") {
      let filteredTags: string[] = [];
      // For other types, use allowed_collection to filter tags
      filteredTags =
        allowed_collection?.length > 0
          ? originalTags.filter((tag) => allowed_collection.includes(tag))
          : []; // Return no tags if allowed_collection is empty

      return {
        ...categoriesdata,
        tags: filteredTags,
      };
    }
    return { ...categoriesdata, tags: originalTags };
  };

  const updatedCategoriesData = getUpdatedCatogories();

  const findSelectedTag = (tag) => {
    return updatedSelectedTags?.includes(tag);
  };

  const addDiscoveredTags = (names: string[]) => {
    if (names.length === 0) return;
    setDiscoveredTags((prev) => {
      const next = names.filter((name) => !prev.includes(name));
      return next.length > 0 ? [...prev, ...next] : prev;
    });
  };

  const categoryLabel = updatedCategoriesData?.category || "";
  const sortedTags: string[] = [...(updatedCategoriesData?.tags || [])].sort();
  const searchTerm = tagSearch.trim();
  const isSearching = searchTerm.length > 0;

  const isCategoryTag = (tag) => sortedTags.includes(tag) || discoveredTags.includes(tag);
  const selectedInCategory: string[] = Array.from(new Set<string>(updatedSelectedTags))
    .filter((tag) => isCategoryTag(tag))
    .sort();
  const tagCount = selectedInCategory.length;
  const withSelected = (tags: string[]) => [
    ...selectedInCategory,
    ...tags.filter((tag) => !selectedInCategory.includes(tag)),
  ];

  const restrictToAllowed = (tags: string[]) => {
    const categoryName = categoryLabel.toLowerCase();
    if (categoryName === "content type") {
      return tags.filter((tag) => allowed_content?.includes(tag));
    }
    if (categoryName === "collection") {
      return tags.filter((tag) => allowed_collection?.includes(tag));
    }
    return tags;
  };

  const runTagSearch = (term: string, start: number, replace: boolean) => {
    const requestId = latestRequestId.current + 1;
    latestRequestId.current = requestId;
    setSearchState((prev) => ({ ...prev, loading: true, failed: false }));

    fetchTagListing({
      searchCategory: categoryLabel,
      searchString: term,
      start,
      rows: TAG_WINDOW_SIZE,
    })
      .then((res: any) => {
        if (latestRequestId.current !== requestId) return;
        const records = Array.isArray(res?.authoring_getTagsList) ? res.authoring_getTagsList : [];
        const names: string[] = records
          .map((record: any) => record?.tag_name || record?.tag_value || "")
          .filter(Boolean);
        const allowedNames = restrictToAllowed(names);
        addDiscoveredTags(allowedNames);
        setSearchState((prev) => ({
          tags: replace ? allowedNames : [...prev.tags, ...allowedNames],
          nextStart: start + TAG_WINDOW_SIZE,
          hasMore: names.length === TAG_WINDOW_SIZE,
          loading: false,
          failed: false,
        }));
      })
      .catch(() => {
        if (latestRequestId.current !== requestId) return;
        setSearchState((prev) => ({ ...prev, hasMore: false, loading: false, failed: true }));
      });
  };

  useEffect(() => {
    if (!isSearching) {
      latestRequestId.current += 1;
      setSearchState({ tags: [], nextStart: 0, hasMore: false, loading: false, failed: false });
      return undefined;
    }
    setSearchState((prev) => ({ ...prev, loading: true }));
    const handle = setTimeout(() => runTagSearch(searchTerm, 0, true), TAG_SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(handle);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchTerm]);

  // A category larger than its initial listing may own selected tags that were never loaded
  // (e.g. a saved filter reopened). Resolve each such tag once with an exact-match lookup so the
  // badge count and pinned selection are right without the editor having to search for it.
  useEffect(() => {
    if (initialTagsState.loading || initialTagsState.complete || !categoryLabel) return;
    const category = categoryLabel;
    selectedTags
      .filter((tag) => !sortedTags.includes(tag) && !resolvedSelectedTags.current.has(tag))
      .forEach((tag) => {
        resolvedSelectedTags.current.add(tag);
        fetchTagListing({
          searchCategory: category,
          searchString: tag,
          start: 0,
          rows: TAG_WINDOW_SIZE,
        })
          .then((res: any) => {
            if (categoriesdata?.category !== category) return;
            const records = Array.isArray(res?.authoring_getTagsList)
              ? res.authoring_getTagsList
              : [];
            const found = records.some(
              (record: any) => (record?.tag_name || record?.tag_value) === tag,
            );
            if (found) addDiscoveredTags([tag]);
          })
          .catch(() => {
            resolvedSelectedTags.current.delete(tag);
          });
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialTagsState, selectedTags, categoryLabel]);

  const localMatches = sortedTags.filter(
    (tag) => String(tag).toLowerCase().includes(searchTerm.toLowerCase()) || findSelectedTag(tag),
  );
  const matchingTags = isSearching
    ? withSelected(searchState.failed ? localMatches : searchState.tags)
    : withSelected(sortedTags);
  // Server results arrive a page at a time, so they are never sliced again here.
  const visibleTags = isSearching ? matchingTags : matchingTags.slice(0, visibleCount);
  const hasMoreTags = isSearching ? searchState.hasMore : matchingTags.length > visibleCount;
  const canCollapseTags = !isSearching && visibleCount > TAG_WINDOW_SIZE;
  const isSearchable = sortedTags.length > TAG_SEARCH_THRESHOLD;
  const isSearchLoading = isSearching && searchState.loading;
  const isInitialLoading = !isSearching && initialTagsState.loading;
  const hasNoMatches = isSearching && !isSearchLoading && matchingTags.length === 0;

  const handleTagSearchChange = (event) => {
    setTagSearch(event.target.value);
    setVisibleCount(TAG_WINDOW_SIZE);
  };

  const clearTagSearch = () => {
    setTagSearch("");
    setVisibleCount(TAG_WINDOW_SIZE);
  };

  const showMoreTags = () => {
    if (isSearching) {
      runTagSearch(searchTerm, searchState.nextStart, false);
      return;
    }
    setVisibleCount((current) => current + TAG_WINDOW_SIZE);
  };

  const showFewerTags = () => {
    setVisibleCount(TAG_WINDOW_SIZE);
  };

  return (
    <Box className={classes.menuAccordianWp}>
      {initialTagsState.loading || updatedCategoriesData?.tags?.length > 0 ? (
        <Accordion
          className='accordianWp'
          id={`${categoryLabel?.toLowerCase()}-tag-listing`}
          sx={{
            ".Platform-x-Paper-root.Platform-x-Paper-elevation": {
              borderRadius: "0px !important",
            },
          }}>
          <AccordionSummary
            className='accordianSummaryWp'
            expandIcon={<KeyboardArrowRightIcon />}
            aria-controls='panel1a-content'
            id='panel1a-header'
            sx={{
              ".Platform-x-Paper-root.Platform-x-Paper-elevation": {
                borderRadius: "0px !important",
              },
              ".Platform-x-AccordionSummary-content": {
                margin: "8px 0",
                padding: "0px 20px 0px 20px",
              },
            }}>
            <Box
              sx={{
                width: "-webkit-fill-available",
                display: "flex",
                alignItems: "center",
              }}>
              <Box
                sx={{
                  width: "fit-content",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  paddingRight: "10px",
                }}>
                <img src={Tag_Icon} alt='TagIcon' />
              </Box>
              <Typography variant='h5medium'>{categoryLabel}</Typography>
              {tagCount > 0 && (
                <Badge
                  data-testid={`tag-count-${categoryLabel?.toLowerCase()}`}
                  classes={{ badge: classes.customBadge }}
                  sx={{
                    margin: "0 0 0 1rem",
                  }}
                  badgeContent={tagCount}></Badge>
              )}
            </Box>
          </AccordionSummary>
          <AccordionDetails
            sx={{
              padding: "0px 20px 0px 20px",
              ".Platform-x-Paper-root.Platform-x-Paper-elevation": {
                borderRadius: "0px !important",
              },
            }}>
            {isSearchable && (
              <Box className={classes.tagSearchField}>
                <SearchIcon sx={{ fontSize: "20px", color: "#57596C", flexShrink: 0 }} />
                <InputBase
                  value={tagSearch}
                  onChange={handleTagSearchChange}
                  placeholder={`${t("search")} ${categoryLabel}`}
                  inputProps={{
                    "aria-label": `${t("search")} ${categoryLabel}`,
                    "data-testid": `tag-search-${categoryLabel?.toLowerCase()}`,
                  }}
                  sx={{ flex: 1, minWidth: 0, fontSize: "14px", color: "#2d2d39" }}
                />
                {tagSearch ? (
                  <IconButton
                    size='small'
                    onClick={clearTagSearch}
                    aria-label={`${t("clear")} ${categoryLabel} ${t("search")}`}
                    data-testid={`tag-search-clear-${categoryLabel?.toLowerCase()}`}
                    sx={{ padding: 0, color: "#8991A5", flexShrink: 0 }}>
                    <CloseRoundedIcon sx={{ fontSize: "18px" }} />
                  </IconButton>
                ) : null}
              </Box>
            )}

            {isSearchLoading || isInitialLoading ? (
              <Box
                data-testid={`tag-search-loading-${categoryLabel?.toLowerCase()}`}
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: "8px",
                  marginBottom: "12px",
                }}>
                <CircularProgress size={14} />
                <Typography variant='p4regular' sx={{ color: "#57596C" }}>
                  {t("loading")}
                </Typography>
              </Box>
            ) : null}

            {hasNoMatches ? (
              <Typography
                variant='p4regular'
                data-testid={`tag-search-empty-${categoryLabel?.toLowerCase()}`}
                sx={{ color: "#57596C", display: "block", marginBottom: "12px" }}>
                {t("no_results")}
              </Typography>
            ) : isInitialLoading ? null : (
              visibleTags.map((tag, index1) => {
                return (
                  <ListItem
                    key={index1}
                    sx={{
                      display: "inline-flex",
                      width: "auto",
                      padding: "0px",
                      marginBottom: "12px",
                    }}>
                    <FormControlLabel
                      value={tag || ""}
                      data-testid={`tag-option-${tag}`}
                      className='vod-cat'
                      control={
                        <Checkbox
                          checked={findSelectedTag(tag)}
                          icon={<CheckCircleOutlineIcon />}
                          checkedIcon={<CheckCircleOutlineIcon />}
                          inputProps={{ id: categoryLabel }}
                        />
                      }
                      label={tag}
                      labelPlacement='start'
                      onChange={(e) => updateTagField(e)}
                      checked={findSelectedTag(tag)}
                    />
                  </ListItem>
                );
              })
            )}

            {(hasMoreTags || canCollapseTags) && (
              <Box sx={{ display: "flex", gap: "12px", marginBottom: "12px" }}>
                {hasMoreTags && (
                  <Link
                    component='button'
                    type='button'
                    underline='always'
                    variant='p3medium'
                    onClick={showMoreTags}
                    data-testid={`tag-view-more-${categoryLabel?.toLowerCase()}`}
                    sx={{
                      color: theme?.palette?.customColor?.TERTIARY,
                      cursor: "pointer",
                      padding: "4px 0",
                    }}>
                    {t("view_more")}
                  </Link>
                )}
                {canCollapseTags && (
                  <Link
                    component='button'
                    type='button'
                    underline='always'
                    variant='p3medium'
                    onClick={showFewerTags}
                    data-testid={`tag-view-less-${categoryLabel?.toLowerCase()}`}
                    sx={{
                      color: theme?.palette?.customColor?.TERTIARY,
                      cursor: "pointer",
                      padding: "4px 0",
                    }}>
                    {t("view_less")}
                  </Link>
                )}
              </Box>
            )}
          </AccordionDetails>
        </Accordion>
      ) : (
        ""
      )}
    </Box>
  );
};

export default GalleryLeftSideBar;
